class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.sideDrawerIds = ['shipping-drawer', 'warranty-drawer'];
    this.enforceDrawerVisibilityState(false);

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.initializeSideDrawerTriggers();
    this.initializeSideDrawerCloseButtons();
    this.initializeOverlayHandlers();
    this._onWarrantyFlushResize = () => {
      const warranty = this.getSideDrawer('warranty-drawer');
      if (warranty && warranty.classList.contains('active')) this.syncWarrantyDrawerFlush(warranty);
    };
    window.addEventListener('resize', this._onWarrantyFlushResize);
  }

  setHeaderCartIconAccessibility() {
    const cartDrawer = this;
    document.querySelectorAll('#cart-icon-bubble, [data-omx-wishlist-drawer-cart]').forEach((cartLink) => {
      if (!cartLink || cartLink.dataset.omxCartDrawerBound === '1') return;
      cartLink.dataset.omxCartDrawerBound = '1';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        cartDrawer.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          cartDrawer.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.enforceDrawerVisibilityState(true);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.openWarrantyDrawerOnCartIfEnabled();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.closeAllSideDrawers(true);
    this.enforceDrawerVisibilityState(false);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  enforceDrawerVisibilityState(isOpen) {
    this.style.visibility = isOpen ? 'visible' : '';
    this.style.pointerEvents = isOpen ? 'auto' : '';
    this.style.display = '';
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.initializeSideDrawerCloseButtons();
      this.initializeOverlayHandlers();
    }, 100);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getSideDrawer(id) {
    return document.getElementById(id);
  }

  getOpenSideDrawer() {
    return this.sideDrawerIds.map((id) => this.getSideDrawer(id)).find((drawer) => drawer && drawer.classList.contains('active'));
  }

  isAnySideDrawerOpen() {
    return Boolean(this.getOpenSideDrawer());
  }

  initializeSideDrawerTriggers() {
    if (this._sideDrawerTriggersInitialized) return;

    this.addEventListener('click', (e) => {
      const shippingTrigger = e.target && e.target.closest('#shipping-info-trigger');
      const warrantyTrigger = e.target && e.target.closest('#warranty-info-trigger');

      if (shippingTrigger) {
        e.preventDefault();
        this.toggleSideDrawer('shipping-drawer');
        return;
      }

      if (warrantyTrigger) {
        e.preventDefault();
        this.toggleSideDrawer('warranty-drawer');
      }
    });

    this._sideDrawerTriggersInitialized = true;
  }

  toggleSideDrawer(drawerId) {
    const drawer = this.getSideDrawer(drawerId);
    if (!drawer) return;

    if (drawer.classList.contains('active')) {
      this.closeSideDrawer(drawerId);
    } else {
      this.openSideDrawer(drawerId);
    }
  }

  openSideDrawer(drawerId) {
    if (!this.classList.contains('active')) return;

    this.sideDrawerIds.forEach((id) => {
      if (id !== drawerId) this.closeSideDrawer(id, true);
    });

    const drawer = this.getSideDrawer(drawerId);
    if (!drawer) return;

    drawer.classList.add('active');
    if (drawerId === 'warranty-drawer') this.syncWarrantyDrawerFlush(drawer);
    this.showOverlay();
  }

  syncWarrantyDrawerFlush(drawer) {
    const panel = drawer || this.getSideDrawer('warranty-drawer');
    if (!panel) return;
    const body = panel.querySelector('.warranty-drawer__body');
    if (this.isMobile()) {
      panel.style.setProperty('top', '61px', 'important');
      panel.style.setProperty('left', '0px', 'important');
      panel.style.setProperty('right', '0px', 'important');
      panel.style.setProperty('width', '100%', 'important');
      panel.style.setProperty('max-width', 'none', 'important');
      panel.style.setProperty('height', 'calc(100dvh - 61px)', 'important');
      if (body) body.style.setProperty('padding', '0px', 'important');
      return;
    }
    this.clearWarrantyDrawerFlush(panel);
  }

  clearWarrantyDrawerFlush(drawer) {
    const panel = drawer || this.getSideDrawer('warranty-drawer');
    if (!panel) return;
    panel.style.removeProperty('top');
    panel.style.removeProperty('left');
    panel.style.removeProperty('right');
    panel.style.removeProperty('width');
    panel.style.removeProperty('max-width');
    panel.style.removeProperty('height');
    const body = panel.querySelector('.warranty-drawer__body');
    if (body) body.style.removeProperty('padding');
  }

  openShippingDrawer() {
    this.openSideDrawer('shipping-drawer');
  }

  closeShippingDrawer(immediate = false) {
    this.closeSideDrawer('shipping-drawer', immediate);
  }

  openWarrantyDrawer() {
    this.openSideDrawer('warranty-drawer');
  }

  openWarrantyDrawerOnCartIfEnabled() {
    if (this.dataset.omxOpenWarranty !== 'true') return;
    this.openWarrantyDrawer();
  }

  closeWarrantyDrawer(immediate = false) {
    this.closeSideDrawer('warranty-drawer', immediate);
  }

  closeSideDrawer(drawerId, immediate = false) {
    const drawer = this.getSideDrawer(drawerId);
    if (!drawer) return;

    if (immediate) {
      drawer.style.transition = 'none';
      drawer.classList.remove('active');
      if (!this.isAnySideDrawerOpen()) this.hideOverlay();
      setTimeout(() => {
        drawer.style.transition = '';
      }, 10);
    } else {
      drawer.classList.remove('active');
      setTimeout(() => {
        if (!this.isAnySideDrawerOpen()) this.hideOverlay();
      }, 300);
    }
  }

  closeAllSideDrawers(immediate = false) {
    this.sideDrawerIds.forEach((id) => this.closeSideDrawer(id, immediate));
  }

  isMobile() {
    return window.innerWidth <= 749;
  }

  showOverlay() {
    if (this.isMobile()) {
      this.showMobileOverlay();
    } else {
      this.showCustomOverlay();
    }
  }

  hideOverlay() {
    if (this.isMobile()) {
      this.hideMobileOverlay();
    } else {
      this.hideCustomOverlay();
    }
  }

  showMobileOverlay() {
    const mobileOverlay = document.getElementById('cart-mobile-overlay');

    if (mobileOverlay) {
      mobileOverlay.style.cssText = `
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        background: rgba(0, 0, 0, var(--omx-cart-overlay-opacity, 0.5)) !important;
        z-index: 1000 !important;
        opacity: 1 !important;
        visibility: visible !important;
        pointer-events: auto !important;
        display: block !important;
      `;

      mobileOverlay.classList.add('active');
    }
  }

  hideMobileOverlay() {
    const mobileOverlay = document.getElementById('cart-mobile-overlay');
    if (mobileOverlay) {
      mobileOverlay.style.opacity = '0';
      mobileOverlay.style.visibility = 'hidden';
      mobileOverlay.style.pointerEvents = 'none';

      mobileOverlay.classList.remove('active');

      setTimeout(() => {
        mobileOverlay.removeAttribute('style');
      }, 300);
    }
  }

  showCustomOverlay() {
    const overlay = document.getElementById('custom-overlay');
    const cartDrawerContainer = document.querySelector('#CartDrawer');

    if (overlay && cartDrawerContainer) {
      cartDrawerContainer.style.setProperty('background-color', 'transparent', 'important');
      overlay.classList.add('active');
    }
  }

  hideCustomOverlay() {
    const overlay = document.getElementById('custom-overlay');
    const cartDrawerContainer = document.querySelector('#CartDrawer');

    if (overlay) {
      overlay.classList.remove('active');
      overlay.style.width = '100vw';
    }

    if (cartDrawerContainer) {
      cartDrawerContainer.style.removeProperty('background-color');
    }
  }

  initializeSideDrawerCloseButtons() {
    const shippingClose = document.getElementById('shipping-drawer-close');
    if (shippingClose && !shippingClose.dataset.omxBound) {
      shippingClose.dataset.omxBound = '1';
      shippingClose.addEventListener('click', () => {
        this.closeShippingDrawer();
      });
    }

    const warrantyClose = document.getElementById('warranty-drawer-close');
    if (warrantyClose && !warrantyClose.dataset.omxBound) {
      warrantyClose.dataset.omxBound = '1';
      warrantyClose.addEventListener('click', () => {
        this.closeWarrantyDrawer();
      });
    }
  }

  initializeOverlayHandlers() {
    const customOverlay = document.getElementById('custom-overlay');
    if (customOverlay && !customOverlay.dataset.omxBound) {
      customOverlay.dataset.omxBound = '1';
      customOverlay.addEventListener('click', () => {
        this.closeBothDrawers();
      });
    }

    const mobileOverlay = document.getElementById('cart-mobile-overlay');
    if (mobileOverlay && !mobileOverlay.dataset.omxBound) {
      mobileOverlay.dataset.omxBound = '1';
      mobileOverlay.addEventListener('click', () => {
        this.closeAllSideDrawers();
      });
    }

    const cartDrawerOverlay = document.getElementById('CartDrawer-Overlay');
    if (cartDrawerOverlay && !cartDrawerOverlay.dataset.omxSideDrawerBound) {
      cartDrawerOverlay.dataset.omxSideDrawerBound = '1';
      cartDrawerOverlay.addEventListener('click', () => {
        if (this.isAnySideDrawerOpen()) {
          this.closeBothDrawers();
        }
      });
    }

    const cartCloseButtons = this.querySelectorAll('.drawer__close');
    cartCloseButtons.forEach((button) => {
      if (button.dataset.omxSideDrawerBound === '1') return;
      button.dataset.omxSideDrawerBound = '1';
      button.addEventListener('click', () => {
        if (this.isAnySideDrawerOpen()) {
          this.closeBothDrawers();
        }
      });
    });
  }

  closeBothDrawers() {
    if (this.isAnySideDrawerOpen()) {
      this.closeAllSideDrawers();

      setTimeout(() => {
        if (this.classList.contains('active')) {
          this.close();
        }
        this.hideOverlay();
      }, 300);
    } else if (this.classList.contains('active')) {
      this.close();
      this.hideOverlay();
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

document.addEventListener('DOMContentLoaded', () => {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.setHeaderCartIconAccessibility === 'function') {
    cartDrawer.setHeaderCartIconAccessibility();
  }
});

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
